import React from 'react';

/** 7세그먼트 비트: a(top) b(ur) c(lr) d(bot) e(ll) f(ul) g(mid) */
const SEG_BITS: Record<string, readonly boolean[]> = {
  '0': [true, true, true, true, true, true, false],
  '1': [false, true, true, false, false, false, false],
  '2': [true, true, false, true, true, false, true],
  '3': [true, true, true, true, false, false, true],
  '4': [false, true, true, false, false, true, true],
  '5': [true, false, true, true, false, true, true],
  '6': [true, false, true, true, true, true, true],
  '7': [true, true, true, false, false, false, false],
  '8': [true, true, true, true, true, true, true],
  '9': [true, true, true, true, false, true, true],
};

const SevenSegGradientContext = React.createContext(false);

const SevenSegmentPaintDefs: React.FC<{ uid: string }> = ({ uid }) => (
  <defs>
    <linearGradient id={`${uid}-on`} x1="16%" y1="6%" x2="88%" y2="94%">
      <stop offset="0%" style={{ stopColor: 'var(--cp-7seg-on-hi)' }} />
      <stop offset="46%" style={{ stopColor: 'var(--cp-7seg-on-mid)' }} />
      <stop offset="100%" style={{ stopColor: 'var(--cp-7seg-on-lo)' }} />
    </linearGradient>
    <linearGradient id={`${uid}-off`} x1="22%" y1="8%" x2="80%" y2="92%">
      <stop offset="0%" style={{ stopColor: 'var(--cp-7seg-off-hi)' }} />
      <stop offset="100%" style={{ stopColor: 'var(--cp-7seg-off-lo)' }} />
    </linearGradient>
  </defs>
);

type SegDigitProps = {
  char: string;
};

/**
 * 육각 세그먼트 — viewBox 0 0 48 80
 */
export const SevenSegmentDigit: React.FC<SegDigitProps> = ({ char }) => {
  const bits = SEG_BITS[char] ?? SEG_BITS['0'];
  const painted = React.useContext(SevenSegGradientContext);
  const uid = React.useId().replace(/:/g, '');
  const segs = [
    { key: 'a', on: bits[0], d: 'M9 7.5 L14.5 2 H33.5 L39 7.5 L33.5 13 H14.5 Z' },
    { key: 'b', on: bits[1], d: 'M39.5 9 L45 14.5 V33 L39.5 38.5 L34 33 V14.5 Z' },
    { key: 'c', on: bits[2], d: 'M39.5 41.5 L45 47 V65.5 L39.5 71 L34 65.5 V47 Z' },
    { key: 'd', on: bits[3], d: 'M9 72.5 L14.5 67 H33.5 L39 72.5 L33.5 78 H14.5 Z' },
    { key: 'e', on: bits[4], d: 'M8.5 41.5 L14 47 V65.5 L8.5 71 L3 65.5 V47 Z' },
    { key: 'f', on: bits[5], d: 'M8.5 9 L14 14.5 V33 L8.5 38.5 L3 33 V14.5 Z' },
    { key: 'g', on: bits[6], d: 'M9 40 L14.5 34.5 H33.5 L39 40 L33.5 45.5 H14.5 Z' },
  ];

  return (
    <svg
      className="cp-dash-7seg-digit"
      viewBox="0 0 48 80"
      width="1em"
      height="1.666em"
      aria-hidden
      focusable="false"
    >
      {painted ? <SevenSegmentPaintDefs uid={uid} /> : null}
      {segs.map((s) => (
        <path
          key={s.key}
          d={s.d}
          className={s.on ? 'cp-dash-7seg-seg is-on' : 'cp-dash-7seg-seg is-off'}
          style={painted ? { fill: `url(#${uid}-${s.on ? 'on' : 'off'})` } : undefined}
        />
      ))}
    </svg>
  );
};

export const SevenSegmentColon: React.FC = () => {
  const painted = React.useContext(SevenSegGradientContext);
  const uid = React.useId().replace(/:/g, '');

  return (
    <svg
      className="cp-dash-7seg-digit cp-dash-7seg-colon"
      viewBox="0 0 18 80"
      width="0.375em"
      height="1.666em"
      aria-hidden
      focusable="false"
    >
      {painted ? <SevenSegmentPaintDefs uid={uid} /> : null}
      <rect
        className="cp-dash-7seg-seg is-on"
        x="4"
        y="22"
        width="10"
        height="10"
        rx="1.2"
        style={painted ? { fill: `url(#${uid}-on)` } : undefined}
      />
      <rect
        className="cp-dash-7seg-seg is-on"
        x="4"
        y="48"
        width="10"
        height="10"
        rx="1.2"
        style={painted ? { fill: `url(#${uid}-on)` } : undefined}
      />
    </svg>
  );
};

type ClockProps = {
  /** `HH:MM:SS` */
  time: string;
  className?: string;
  ariaLabel?: string;
};

/** HH:MM:SS 7세그먼트 시계 */
export const SevenSegmentClock: React.FC<ClockProps> = ({ time, className, ariaLabel }) => {
  const digits = time.replace(/[^\d]/g, '').padStart(6, '0').slice(-6);
  const groups = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)];

  return (
    <SevenSegGradientContext.Provider value={true}>
      <div
        className={['cp-dash-7seg', 'cp-dash-7seg-clock', className].filter(Boolean).join(' ')}
        aria-label={ariaLabel ?? time}
      >
        {groups.map((pair, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 ? <SevenSegmentColon /> : null}
            <span className="cp-dash-7seg-clock-group">
              <SevenSegmentDigit char={pair[0] ?? '0'} />
              <SevenSegmentDigit char={pair[1] ?? '0'} />
            </span>
          </React.Fragment>
        ))}
      </div>
    </SevenSegGradientContext.Provider>
  );
};
